import { useState, useEffect, useMemo } from 'react';
import {
  useProduct,
  useBulkUpdateVariantName,
  useRemoveVariant,
  useDisbandVariantGroup,
} from '../../hooks/useProducts';
import {
  Button,
  Modal,
  ControlledInput,
  DoubleCheckModal,
  QuietAddTrigger,
  CachedImage,
} from '@shared/ui';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { notifications } from '@mantine/notifications';
import { Loader } from '@mantine/core';
import AddVariantModal from '../AddVariantModal';
import { productThumbSrc } from '../../utils';
import './ManageVariantsModal.css';
import '@shared/styles/page.css';

interface ManageVariantsModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number;
}

interface VariantFormData {
  [key: string]: string | undefined;
}

export default function ManageVariantsModal({
  isOpen,
  onClose,
  productId,
}: ManageVariantsModalProps) {
  const { data: product, isLoading, refetch } = useProduct(productId);
  const [removingVariantId, setRemovingVariantId] = useState<number | null>(null);
  const [isDeleteVariantModalOpen, setIsDeleteVariantModalOpen] = useState(false);
  const [variantToDelete, setVariantToDelete] = useState<number | null>(null);
  const [isDisbandGroupModalOpen, setIsDisbandGroupModalOpen] = useState(false);
  const [showAddVariantModal, setShowAddVariantModal] = useState(false);

  const bulkUpdateVariantNameMutation = useBulkUpdateVariantName();
  const removeVariantMutation = useRemoveVariant();
  const disbandVariantGroupMutation = useDisbandVariantGroup();

  const variants = product?.variants || [];

  const variantSchema = useMemo(() => {
    const shape: Record<string, yup.StringSchema> = {};
    variants.forEach((variant) => {
      const fieldName = `variant_${variant.id}`;
      shape[fieldName] = yup.string().required('Название варианта обязательно');
    });
    return yup.object(shape);
  }, [variants]);

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<VariantFormData>({
    resolver: yupResolver(variantSchema) as any,
    defaultValues: {},
  });

  useEffect(() => {
    const initialValues: VariantFormData = {};
    variants.forEach((variant) => {
      const raw = (variant.variantName || '').trim();
      // Не подставляем название товара как имя варианта
      initialValues[`variant_${variant.id}`] =
        raw && raw !== variant.name.trim() ? raw : '';
    });
    reset(initialValues);
  }, [variants, reset]);

  useEffect(() => {
    if (!isOpen) {
      setShowAddVariantModal(false);
    }
  }, [isOpen]);

  const handleSaveAllChanges = handleSubmit(async (data) => {
    const updates = variants.map((variant) => {
      const fieldName = `variant_${variant.id}`;
      return {
        productId: variant.id,
        variantName: (data[fieldName] || '').trim(),
      };
    });

    try {
      await bulkUpdateVariantNameMutation.mutateAsync({ updates });
      notifications.show({
        title: 'Успешно',
        message: 'Названия вариантов обновлены',
        color: 'green',
      });
      onClose();
    } catch {
      notifications.show({
        title: 'Ошибка',
        message: 'Ошибка при обновлении названий вариантов',
        color: 'red',
      });
    }
  });

  const handleRemoveVariant = (variantId: number) => {
    setVariantToDelete(variantId);
    setIsDeleteVariantModalOpen(true);
  };

  const confirmRemoveVariant = async () => {
    if (!variantToDelete) return;

    setRemovingVariantId(variantToDelete);
    setIsDeleteVariantModalOpen(false);

    try {
      await removeVariantMutation.mutateAsync({
        variantId: variantToDelete,
        productId,
      });
      notifications.show({
        title: 'Успешно',
        message: 'Вариант удален из подборки',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: 'Ошибка при удалении варианта',
        color: 'red',
      });
      console.error(error);
    } finally {
      setRemovingVariantId(null);
      setVariantToDelete(null);
    }
  };

  const cancelRemoveVariant = () => {
    setIsDeleteVariantModalOpen(false);
    setVariantToDelete(null);
  };

  const confirmDisbandGroup = async () => {
    setIsDisbandGroupModalOpen(false);
    try {
      await disbandVariantGroupMutation.mutateAsync(productId);
      notifications.show({
        title: 'Успешно',
        message: 'Подборка разформирована',
        color: 'green',
      });
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: 'Ошибка при разформировании подборки',
        color: 'red',
      });
      console.error(error);
    }
  };

  const handleAddVariantSuccess = async () => {
    setShowAddVariantModal(false);
    await refetch();
  };

  const getContent = () => {
    if (isLoading) {
      return (
        <div className="empty-container">
          <Loader />
        </div>
      );
    }

    return (
      <div className="manage-variants-content">
        {variants.length > 0 && (
          <p className="manage-variants-hint">
            Укажите названия вариантов — они будут отображаться под карточками вариантов.
          </p>
        )}

        {!variants.length ? (
          <div className="manage-variants-empty">
            Пока нет вариантов. Добавьте новый товар или свяжите существующий.
          </div>
        ) : (
          <div className="manage-variants-list">
            {variants.map((variant) => {
              const fieldName = `variant_${variant.id}`;
              const previewImage =
                variant.images && variant.images.length > 0 ? variant.images[0] : null;
              return (
                <div key={variant.id} className="manage-variant-item">
                  <div className="manage-variant-preview">
                    {previewImage ? (
                      <CachedImage
                        src={productThumbSrc(previewImage) || previewImage.url}
                        alt={variant.name}
                        loading="lazy"
                      />
                    ) : (
                      <div className="manage-variant-preview__empty" aria-hidden />
                    )}
                  </div>
                  <div className="manage-variant-header">
                    <div className="manage-variant-top">
                      <div className="manage-variant-name">{variant.name}</div>
                      <button
                        type="button"
                        className="manage-variant-remove"
                        onClick={() => handleRemoveVariant(variant.id)}
                        disabled={removingVariantId === variant.id}
                      >
                        {removingVariantId === variant.id ? 'Удаление…' : 'Удалить'}
                      </button>
                    </div>
                    <p className="manage-variant-field-label">Название варианта</p>
                    <div className="manage-variant-input">
                      <ControlledInput
                        name={fieldName}
                        control={control}
                        error={errors[fieldName]}
                        placeholder="Например: Красные, 25 шт"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {variants.length > 0 && (
          <div className="manage-variants-actions">
            <button
              type="button"
              className="manage-variants-disband"
              onClick={() => setIsDisbandGroupModalOpen(true)}
              disabled={disbandVariantGroupMutation.isPending}
            >
              {disbandVariantGroupMutation.isPending
                ? 'Разформирование…'
                : 'Разформировать подборку'}
            </button>
            <div className="manage-variants-actions__right">
              <Button
                onClick={handleSaveAllChanges}
                disabled={bulkUpdateVariantNameMutation.isPending}
                loading={bulkUpdateVariantNameMutation.isPending}
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <div className="manage-variants-modal-title">
            <span>Настройка вариантов</span>
            <QuietAddTrigger onClick={() => setShowAddVariantModal(true)}>
              Добавить вариант
            </QuietAddTrigger>
          </div>
        }
        maxWidth={800}
      >
        {getContent()}
        {isDeleteVariantModalOpen && variantToDelete && (
          <DoubleCheckModal
            isOpen={isDeleteVariantModalOpen}
            onClose={cancelRemoveVariant}
            title="Удалить вариант из подборки?"
            message="Вы уверены, что хотите удалить этот вариант из подборки?"
            secondButtonText="Удалить"
            secondButtonAction={confirmRemoveVariant}
            isLoading={removeVariantMutation.isPending}
          />
        )}
        {isDisbandGroupModalOpen && (
          <DoubleCheckModal
            isOpen={isDisbandGroupModalOpen}
            onClose={() => setIsDisbandGroupModalOpen(false)}
            title="Разформировать подборку?"
            message="Вы уверены, что хотите разформировать всю подборку? Все варианты будут удалены из группы."
            secondButtonText="Разформировать"
            secondButtonAction={confirmDisbandGroup}
            isLoading={disbandVariantGroupMutation.isPending}
          />
        )}
      </Modal>

      <AddVariantModal
        isOpen={showAddVariantModal}
        onClose={() => setShowAddVariantModal(false)}
        sourceProductId={productId}
        onSuccess={handleAddVariantSuccess}
      />
    </>
  );
}
